import { useState, useEffect } from 'react'
import { useHistory } from 'react-router'
import { useQueryParam } from 'use-query-params'
import { Grid, Typography, makeStyles, Container, Button } from '@material-ui/core'
import GetAppRoundedIcon from '@material-ui/icons/GetAppRounded'
import { convertDateToFormatTH, convertDateToFormatEN } from '../utilities/date'
import useTranslate from '../i18n/useTranslate'
import RiskProjectSummaryPanel from '../component/report/risk-project-summary/Panel'
import riskProjectSummaryExport from '../component/report/risk-project-summary/report'
import useAuthenStore from '../stores/authen/authenStore'
import SearchAccordion from '../control/accordion/SearchAccordion'
import Table from '../component/report/risk-project-summary/Table'
import useLoading from '../control/loading/useLoading'
import {
    useSearchRiskProjectSummary,
    RiskProjectRequest,
    RiskProject,
} from '../services/um/useSearchRiskProjectSummary'

const RiskProjectSummaryForm = () => {
    const translate = useTranslate()
    const lang = translate.languageStore.currentLanguage as 'th' | 'en'
    const classes = useStyles()
    const history = useHistory()
    const authenStore = useAuthenStore()
    const loadingHook = useLoading()
    const { searchHighRiskProject } = useSearchRiskProjectSummary()

    const [dataList, setDataList] = useState<RiskProject[]>([])
    const [keyword, setKeyword] = useQueryParam<string | undefined>('keyword')

    const setSearchParam = (value: RiskProjectRequest) => {
        setKeyword(value.keyword)
    }

    const [initialValue, setInitialValue] = useState<RiskProjectRequest>({
        keyword: keyword,
        userID: authenStore.profile.userId ?? 0,
    })

    useEffect(() => {
        if (authenStore.profile.userId) {
            onSubmit(initialValue)
        }
    }, [authenStore.profile.userId])

    const onSubmit = async (value: RiskProjectRequest) => {
        loadingHook.loadingOpen()
        setInitialValue(value)
        const data = await searchHighRiskProject({
            keyword: value.keyword,
            userID: authenStore.profile.userId ?? 0,
        })
        setDataList(data)
        setSearchParam(value)
        loadingHook.loadingClose()
    }

    const onClear = async (form: any) => {
        form.reset()
        setInitialValue({
            userID: authenStore.profile.userId ?? 0,
        })
    }

    const convertDate = (date: string | null) => {
        if (lang === 'th' && date) {
            return convertDateToFormatTH(date)
        } else if (lang === 'en' && date) {
            return convertDateToFormatEN(date)
        } else {
            return '-'
        }
    }

    const onBack = () => {
        history.push('/dashboard')
    }

    const handleExportExcel = () => {
        if (!dataList.length) return
        riskProjectSummaryExport({
            header: {
                keyword: keyword ?? '-',
            },
            content: dataList.map((item) => ({
                projectID: item.projectNumber,
                receiveDate: convertDate(item.receiveDate),
                endDate: convertDate(item.closedDate),
                researcherName: item.name?.[lang] ?? '-',
                projectNameTH: item.projectName?.['th'] ?? '-',
                projectNameEN: item.projectName?.['en'] ?? '-',
                projectType: item.projectType?.name?.[lang] ?? '-',
            })),
            translate: (value: string) => translate.get(value),
        })
    }

    return (
        <Container maxWidth="lg">
            <Grid className={classes.root}>
                <SearchAccordion
                    title={translate.get('UM.RISK_PROJECT_SUMMARY.HEADER')}
                    isBack={true}
                    onClickBack={onBack}
                >
                    <RiskProjectSummaryPanel onSubmit={onSubmit} initialValue={initialValue} onClear={onClear} />
                </SearchAccordion>
                <Grid container justify="space-between" alignItems="flex-end" className={classes.container}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="h6">
                            {translate.get('LABEL.SEARCH_TOTAL_RESULT', [`${dataList.length}`])}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Grid container justify="flex-end" spacing={2}>
                            <Grid item>
                                <Button
                                    size="large"
                                    variant="outlined"
                                    color="primary"
                                    startIcon={
                                        <GetAppRoundedIcon style={{ color: dataList.length ? '#DB5F8E' : '#e0e0e0' }} />
                                    }
                                    disabled={!dataList.length}
                                    onClick={handleExportExcel}
                                >
                                    <Typography variant="button">{translate.get('BUTTON.EXPORT')}</Typography>
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid item xs={12}>
                        <Table dataList={dataList} />
                    </Grid>
                </Grid>
            </Grid>
        </Container>
    )
}

const useStyles = makeStyles(() => ({
    root: {
        width: '100%',
    },
    container: {
        margin: '15px 0px',
        width: '100%',
    },
    header: {
        marginBottom: '16px',
        fontWeight: 'bold',
    },
}))

export default RiskProjectSummaryForm
